import * as React from "react";

import { cn } from "@/common/helpers/utils";

const Table = ({ className, ref, ...props }: React.ComponentProps<"table">) => (
  <table ref={ref} className={cn("w-full caption-bottom border-spacing-0 text-sm", className)} {...props} />
);
Table.displayName = "Table";

const TableHeader = ({ className, ref, ...props }: React.ComponentProps<"thead">) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
);
TableHeader.displayName = "TableHeader";

const TableBody = ({ className, ref, ...props }: React.ComponentProps<"tbody">) => (
  <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
);
TableBody.displayName = "TableBody";

const TableFooter = ({ className, ref, ...props }: React.ComponentProps<"tfoot">) => (
  <tfoot ref={ref} className={cn("bg-primary/50 border-t font-medium [&>tr]:last:border-b-0", className)} {...props} />
);
TableFooter.displayName = "TableFooter";

const TableRow = ({ className, ref, ...props }: React.ComponentProps<"tr">) => (
  <tr
    ref={ref}
    className={cn(
      "border-border h-full border-b transition-all duration-200",
      "hover:bg-muted/30",
      "data-[state=selected]:bg-primary/5 data-[state=selected]:border-primary/20",
      "ring-offset-background",
      "group",
      className,
    )}
    {...props}
  />
);
TableRow.displayName = "TableRow";

interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  className?: string;
  ref?: React.Ref<HTMLTableCellElement>;
}

const TableHead = ({ className, ref, ...props }: TableHeadProps) => (
  <th
    ref={ref}
    className={cn(
      "text-muted-foreground h-9 px-4 text-left align-middle text-xs font-medium tracking-wider uppercase",
      "border-border sticky top-0 z-10 border-b",
      "[&:has([role=checkbox])]:pr-0 [&:has([role=checkbox])]:pl-4",
      "first:rounded-tl-lg last:rounded-tr-lg",
      className,
    )}
    {...props}
  />
);
TableHead.displayName = "TableHead";

interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  className?: string;
  ref?: React.Ref<HTMLTableCellElement>;
}

const TableCell = ({ className, ref, ...props }: TableCellProps) => (
  <td
    ref={ref}
    className={cn(
      "h-full px-4 py-2 align-middle text-sm leading-5",
      "[&:has([role=checkbox])]:pr-0 [&:has([role=checkbox])]:pl-4",
      "group-hover:via-muted/10 group-hover:bg-linear-to-r group-hover:from-transparent group-hover:to-transparent",
      "transition-all duration-200",
      className,
    )}
    {...props}
  />
);
TableCell.displayName = "TableCell";

const TableCaption = ({ className, ref, ...props }: React.ComponentProps<"caption">) => (
  <caption ref={ref} className={cn("text-muted-foreground mt-4 text-sm", className)} {...props} />
);
TableCaption.displayName = "TableCaption";

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow };
